import React from 'react'
import { Button, Element, Icon } from 'react-bulma-components'
import { FiltersResourcesEnum, initialFilters, FiltersState } from '../../store/filters'
import useFilters from '../../hooks/filters'
import { RemoveIcon } from '../icons'
import { isEqual } from 'lodash'

interface FiltersWrapperProps<Resource extends FiltersResourcesEnum> {
  resource: Resource
  children: React.ReactNode
  preservedFields?: Array<keyof FiltersState[Resource]>
}

/**
 * Wrapper component for filters that adds a reset button
 * @param resource - The resource type for the filters
 * @param children - The filter components to wrap
 * @param preservedFields - Fields to keep at their current value when resetting
 */
const FiltersWrapper = <Resource extends FiltersResourcesEnum>({
  resource,
  children,
  preservedFields = [],
}: FiltersWrapperProps<Resource>): React.ReactNode => {
  const { setFilters, filters } = useFilters(resource)

  /**
   * Resets the filters to their initial state from the store
   */
  const handleReset = () => {
    const initialFilterValues = initialFilters[resource]
    const currentFilterValues = filters

    // Keep ignored fields at their current value
    const resetValues = {
      ...initialFilterValues,
      ...preservedFields.reduce(
        (acc, field) => {
          acc[field] = currentFilterValues[field]
          return acc
        },
        {} as Partial<FiltersState[Resource]>,
      ),
    }

    setFilters(resource, resetValues)
  }

  /**
   * Checks if all non-preserved fields are at their initial values
   * @returns true if all non-preserved fields match their initial values
   */
  const isCurrentValueIsInitial = (
    Object.keys(filters) as Array<keyof FiltersState[Resource]>
  ).every(field => {
    // Skip preserved fields in the comparison
    if (preservedFields.includes(field)) {
      return true
    }
    return isEqual(filters[field], initialFilters[resource][field])
  })

  return (
    <Element style={{ position: 'relative', width: '100%' }}>
      <Element style={{ width: '100%' }}>{children}</Element>
      <Element style={{ position: 'absolute', right: 0, top: 6 }}>
        <Button
          onClick={handleReset}
          data-test="reset-filters-button"
          size="small"
          disabled={isCurrentValueIsInitial}
          color={isCurrentValueIsInitial ? 'default' : 'primary'}
          outlined={!isCurrentValueIsInitial}
        >
          <Icon>
            <RemoveIcon />
          </Icon>
          <Element renderAs="span">Réinitialiser les filtres</Element>
        </Button>
      </Element>
    </Element>
  )
}

export default FiltersWrapper
